import type {
  Clientize,
  ChecklistDocumentView,
  ChecklistItemStatus,
  ChecklistPendingFlagDecision,
  EvaluationState,
} from 'types';

import { api } from './api';

interface UpdateChecklistItemRequest {
  checklistId: string;
  itemKey: string;
  status: ChecklistItemStatus;
  notes?: string;
  data?: Record<string, string | boolean | ChecklistPendingFlagDecision[]>;
  evaluations?: Record<string, EvaluationState>;
}

interface CheckEvaluationRequest {
  checklistId: string;
  itemKey: string;
  inputKey: string;
  entered: Record<string, unknown>;
}

export const checklistsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getChecklistByQueueId: build.query<Clientize<ChecklistDocumentView>, string>({
      query: (queueId) => ({ url: `/checklists?queueId=${queueId}` }),
      providesTags: (result, _error, queueId) => [
        { type: 'Checklists' as const, id: `queue-${queueId}` },
        ...(result ? [{ type: 'Checklists' as const, id: result.id }] : []),
      ],
    }),

    getChecklistsByAccountId: build.query<Clientize<ChecklistDocumentView>[], string>({
      query: (accountId) => ({ url: `/checklists/by-account/${accountId}` }),
      transformResponse: (response: { data: Clientize<ChecklistDocumentView>[] }) => response.data || [],
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Checklists' as const, id })), { type: 'Checklists', id: 'LIST' }]
          : [{ type: 'Checklists', id: 'LIST' }],
    }),

    updateChecklistItem: build.mutation<Clientize<ChecklistDocumentView>, UpdateChecklistItemRequest>({
      query: ({ checklistId, itemKey, status, notes, data, evaluations }) => ({
        url: `/checklists/${checklistId}/items/${itemKey}`,
        method: 'PATCH',
        body: {
          status,
          ...(notes && { notes }),
          ...(data && { data }),
          ...(evaluations && { evaluations }),
        },
      }),
      invalidatesTags: (result) => (result ? [{ type: 'Checklists' as const, id: result.id }] : []),
    }),

    completeChecklist: build.mutation<Clientize<ChecklistDocumentView>, { checklistId: string }>({
      query: ({ checklistId }) => ({
        url: `/checklists/${checklistId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (result) => (result ? [{ type: 'Checklists' as const, id: result.id }] : []),
    }),

    checkEvaluation: build.mutation<Clientize<EvaluationState>, CheckEvaluationRequest>({
      query: ({ checklistId, itemKey, inputKey, entered }) => ({
        url: `/checklists/${checklistId}/items/${itemKey}/inputs/${inputKey}/check`,
        method: 'POST',
        body: { entered },
      }),
    }),

    replaceChecklist: build.mutation<Clientize<ChecklistDocumentView>, { checklistId: string; queueId: string }>({
      query: ({ checklistId }) => ({ url: `/checklists/${checklistId}/replace`, method: 'POST' }),
      invalidatesTags: (result, error, { queueId }) => [
        { type: 'Checklists' as const, id: `queue-${queueId}` },
        ...(result ? [{ type: 'Checklists' as const, id: result.id }] : []),
      ],
    }),
  }),
});

export const {
  useGetChecklistByQueueIdQuery,
  useGetChecklistsByAccountIdQuery,
  useUpdateChecklistItemMutation,
  useCompleteChecklistMutation,
  useCheckEvaluationMutation,
  useReplaceChecklistMutation,
} = checklistsApi;
